import { filterUrl } from "@/telemetry/filterDomain";
import { logError } from "@/utils/log";
import { isMobileView, pdfWidthMapping } from "@/utils/screen";
import { range } from "lodash";
import { ReactNode, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Card, VBox } from "./Box";
import { LoadingIndicator } from "./LoadingIndicator";
import { isPdfFetchFailure } from "./pdfLoadError";
import { pdfjs_version } from "./pdfjs.version";
import { useSize } from "./useSize";

pdfjs.GlobalWorkerOptions.workerSrc = pdfjs_version;

type PdfFile = { url: string } | { data: ArrayBuffer | Blob };

export type PdfPreviewProps = {
  file: PdfFile;
  width?: "sm" | "md" | "lg" | "auto" | number;
  extraContent?: ReactNode;
  action?: ReactNode;
};

function loggableUrl(file: PdfFile): string | undefined {
  if (!("url" in file)) {
    return undefined;
  }
  return file.url.startsWith("data:") ? "data:" : filterUrl(file.url);
}

function reportPdfPreviewError(file: PdfFile, error: unknown, context: string) {
  if (isPdfFetchFailure(error)) {
    return;
  }
  logError(error, `PdfPreview: ${context}`, { url: loggableUrl(file) });
}

export function PdfPreview({ file, width = "lg", extraContent, action }: PdfPreviewProps) {
  const [numPages, setNumPages] = useState(0);
  const { setRef, size } = useSize({ measureParent: false });
  const resolvedWidth = width === "auto" ? (size.width > 0 ? size.width : undefined) : width;
  return (
    <div ref={setRef} className="w-full">
      {resolvedWidth !== undefined && (
        <Document
          file={"url" in file ? file.url : file.data}
          loading={<LoadingIndicator />}
          onLoadError={(error) => reportPdfPreviewError(file, error, "loading failed")}
          onSourceError={(error) => reportPdfPreviewError(file, error, "reading source failed")}
          onLoadSuccess={({ numPages }) => setNumPages(numPages)}
        >
          <VBox gap={"md"} alignItems={"center"}>
            {range(numPages).map((i) => (
              <Card key={i} dropShadow rounded={false} padding={false}>
                {action}
                <Page
                  pageIndex={i}
                  scale={typeof resolvedWidth === "number" ? undefined : isMobileView() ? 0.3 : 0.9}
                  width={
                    typeof resolvedWidth === "number"
                      ? resolvedWidth
                      : pdfWidthMapping[resolvedWidth]
                  }
                />
              </Card>
            ))}
            {extraContent}
          </VBox>
        </Document>
      )}
    </div>
  );
}
